import { ItemKind } from "dto/IKindItems";

/** Rodzaj dekretu (odpowiednik BE DecreeType — grupa „Normal"). */
export enum EDecreeType {
  /** „Przekaż" (opisowy) — transfer własności. */
  Custom = 0,
  /** „Przygotować odpowiedź" — transfer + termin. */
  ReplyDeadline = 1,
  /** „Prośba o konsultację" — kopia, bez utraty prowadzenia. */
  ConsultationRequest = 2,
  /** „Przesłać według właściwości" — transfer do wskazanego odbiorcy. */
  ByCharacteristic = 3,
  /** „Do wiadomości" — kopia informacyjna. */
  CourtesyCopy = 4,
  /** „Do wiadomości" do wielu. */
  CourtesyCopyWithMultipleUsers = 5,
  /** „Do wiadomości za potwierdzeniem" — kopia, którą odbiorca musi potwierdzić. */
  CourtesyCopyWithConfirmation = 6,
  /** „Zwrot" — przesyłka wraca do autora dekretu / biura podawczego (tylko z BE). */
  Return = 7,
}

/** Priorytet dekretu (odpowiednik BE DecreePriority). */
export enum EDecreePriority {
  Low = -1,
  Normal = 0,
  High = 1,
}

/** Typy przenoszące własność przesyłki (reszta to kopia „do wiadomości"). */
export const DECREE_TRANSFER_TYPES: EDecreeType[] = [
  EDecreeType.Custom,
  EDecreeType.ReplyDeadline,
  EDecreeType.ByCharacteristic,
  EDecreeType.Return,
];

export const decreeTransfersOwnership = (type: EDecreeType): boolean =>
  DECREE_TRANSFER_TYPES.includes(type);

export interface IDecree {
  Id: string;
  ObjectId: string;
  Kind: ItemKind;
  Type: EDecreeType;
  Priority: EDecreePriority;
  CreatedByUserId: string;
  CreatedByLabel: string | null;
  ReceiverUserId: string | null;
  ReceiverLabel: string | null;
  Deadline: string | null;
  Title: string | null;
  Text: string | null;
  TransfersOwnership: boolean;
  IsUnread: boolean;
  Inactive: boolean;
  IsClosed: boolean;
  IsPartlyClosed: boolean;
  PartlyClosedDescription: string | null;
  ClosedByUserId: string | null;
  ClosedByLabel: string | null;
  ClosingDate: string | null;
  CreationTimestamp: string;
  IsExpired: boolean;
  IsAboutToExpire: boolean;
  /** Kopia „do wiadomości za potwierdzeniem" — czeka na potwierdzenie odbiorcy. */
  RequiresConfirmation?: boolean;
  ConfirmationTimestamp?: string | null;
  /** Dla zwrotu: dekret, na który zwrot odpowiada. */
  ReturnedDecreeId?: string | null;
}

/** Pozycja skrzynki dekretów (Do wykonania / Wydane). */
export interface IDecreeBoxItem extends IDecree {
  ParcelTitle: string | null;
  ParcelNumber: string | null;
  ParcelSender: string | null;
  ParcelReceiveDate: string | null;
  ParcelOwnerUserId: string | null;
  ParcelOwnerLabel: string | null;
  TypeLabel: string | null;
}

export interface IDecreeBoxResponse {
  Items: IDecreeBoxItem[];
  /** Otwarte dekrety do wykonania przeze mnie (badge w menu). */
  OpenReceivedCount: number;
}

export interface IReturnParcel {
  ParcelId: string;
  Reason: string;
}

export interface IConfirmDecree {
  DecreeId: string;
}

export interface IDecreeKey {
  Id: string;
  Key: string;
}

export interface ICreateDecree {
  ParcelId: string;
  ReceiverUserId: string;
  Type: EDecreeType;
  Priority: EDecreePriority;
  Deadline?: string | null;
  Title?: string | null;
  Text?: string | null;
  Keys?: IDecreeKey[];
}

export interface ICloseDecree {
  DecreeId: string;
  IsPartlyClosed: boolean;
  PartlyClosedDescription?: string | null;
  ClosingDate: string;
}

export interface IDecreeListResponse {
  Decrees: IDecree[];
  /** ObjectKeyId, które FE musi ponownie zaszyfrować kluczem publicznym odbiorcy. */
  RequiredKeyIds: string[];
}

/** Etykiety typów dekretów (spójne z BE [Description]). */
export const DECREE_TYPE_LABELS: Record<EDecreeType, string> = {
  [EDecreeType.Custom]: "Przekaż",
  [EDecreeType.ReplyDeadline]: "Przygotować odpowiedź",
  [EDecreeType.ConsultationRequest]: "Prośba o konsultację",
  [EDecreeType.ByCharacteristic]: "Przesłać według właściwości",
  [EDecreeType.CourtesyCopy]: "Do wiadomości",
  [EDecreeType.CourtesyCopyWithMultipleUsers]: "Do wiadomości (wielu)",
  [EDecreeType.CourtesyCopyWithConfirmation]: "Do wiadomości za potwierdzeniem",
  [EDecreeType.Return]: "Zwrot",
};

export const DECREE_PRIORITY_LABELS: Record<EDecreePriority, string> = {
  [EDecreePriority.Low]: "Niski",
  [EDecreePriority.Normal]: "Zwykły",
  [EDecreePriority.High]: "Wysoki",
};
